import { useState, useEffect, useRef, useCallback } from "react";
import { Dices, Flame } from "lucide-react";
import { toast } from "sonner";
import { send } from "@/lib/deriv";
import { api } from "@/lib/api";
import HiLoCardsAuto from "@/components/dashboard/HiLoCardsAuto";
import CoinCombat from "@/components/dashboard/CoinCombat";
import MarketScanner from "@/components/arcade/MarketScanner";

/**
 * 🎴 HiLo Cards — the market's last digit becomes a playing card. Tap
 * HIGHER or LOWER and a REAL Deriv Digit Over/Under contract fires with
 * the card as the barrier. Payout changes with the card (HIGHER on an 8
 * pays big, HIGHER on a 1 pays small). Ties (same digit) lose — that's
 * the house edge, shown honestly. Trades land in the normal History.
 */
const SYMBOL = "1HZ100V"; // Vol 100 (1s) — 2-decimal quotes, 1s ticks
const lastDigit = (p) => Number(Number(p).toFixed(2).slice(-1));
const SUITS = ["♠", "♥", "♦", "♣"];

const HiLoCards = ({ authorized, accountId, accessToken, currency = "USD", onConnect }) => {
  const [card, setCard] = useState(null);
  const [flip, setFlip] = useState(false);
  const [stake, setStake] = useState("1.00");
  const [inFlight, setInFlight] = useState(null); // { side }
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(() => Number(localStorage.getItem("hilocards_best") || 0));
  const [history, setHistory] = useState([]);
  const [pays, setPays] = useState({ hi: null, lo: null });
  const [net, setNet] = useState(0);
  const suitRef = useRef(SUITS[0]);
  const flightRef = useRef(false);
  // 🎯 pair filter — Signal Engine routes every play to the strongest market
  const symRef = useRef(SYMBOL);
  const [symLabel, setSymLabel] = useState("V100 1s");
  const onScan = useCallback((row) => { symRef.current = row.sym; setSymLabel(row.label); }, []);

  // 🂠 live table — poll the latest tick while idle so the card stays fresh
  useEffect(() => {
    if (!authorized) return undefined;
    let dead = false;
    const poll = async () => {
      if (dead || flightRef.current) return;
      try {
        const res = await send({ ticks_history: symRef.current, count: 1, end: "latest", style: "ticks" });
        const p = res?.history?.prices?.[0];
        if (p == null || dead || flightRef.current) return;
        const d = lastDigit(p);
        setCard((prev) => {
          if (prev !== d) {
            suitRef.current = SUITS[Math.floor(Math.random() * 4)];
            setFlip((f) => !f);
          }
          return d;
        });
      } catch { /* next poll */ }
    };
    poll();
    const t = setInterval(poll, 2500);
    return () => { dead = true; clearInterval(t); };
  }, [authorized]);

  // 💰 payout preview for both sides of the current card
  const quoteCacheRef = useRef({});
  useEffect(() => {
    if (!authorized || card == null || inFlight) { return undefined; }
    let dead = false;
    const amt = Math.max(0.35, Number(stake) || 0.35);
    // 🚦 payout ratios barely move — cache per card/stake/symbol for 30s so we
    // don't burn Deriv's proposal rate limit twice per card flip
    const key = `${symRef.current}|${card}|${amt}`;
    const hit = quoteCacheRef.current[key];
    if (hit && Date.now() - hit.t < 30000) { setPays(hit.pays); return undefined; }
    const quote = async (ct) => {
      try {
        // Deriv's new Options API expects `underlying_symbol` (legacy `symbol` is rejected)
        const r = await send({ proposal: 1, amount: amt, basis: "stake", currency, contract_type: ct, duration: 1, duration_unit: "t", underlying_symbol: symRef.current, barrier: String(card) });
        return r?.proposal?.payout ? Math.round((r.proposal.payout / amt) * 100) / 100 : null;
      } catch { return null; }
    };
    (async () => {
      const [hi, lo] = await Promise.all([
        card >= 9 ? Promise.resolve(null) : quote("DIGITOVER"),
        card <= 0 ? Promise.resolve(null) : quote("DIGITUNDER"),
      ]);
      if (!dead) { setPays({ hi, lo }); quoteCacheRef.current[key] = { t: Date.now(), pays: { hi, lo } }; }
    })();
    return () => { dead = true; };
  }, [authorized, card, stake, inFlight, currency, symLabel]);

  const play = useCallback(async (side) => {
    if (!authorized) { onConnect?.(); return; }
    if (card == null || flightRef.current) return;
    if ((side === "hi" && card >= 9) || (side === "lo" && card <= 0)) return;
    const amt = Math.max(0.35, Math.round((Number(stake) || 0.35) * 100) / 100);
    const ct = side === "hi" ? "DIGITOVER" : "DIGITUNDER";
    flightRef.current = true;
    setInFlight({ side });
    const played = card;
    const playSym = symRef.current; // 🎯 lock the scanner's pick for this whole play
    try {
      // 🚀 zero-proposal buy — parameters buy, same as the arcade engine
      const params = { amount: amt, basis: "stake", currency, contract_type: ct, duration: 1, duration_unit: "t", underlying_symbol: playSym, barrier: String(played) };
      let buy = await send({ buy: 1, price: amt, parameters: params });
      for (const wait of [2000, 5000]) {
        if (!(buy?.error && /rate limit/i.test(buy.error.message || ""))) break;
        await new Promise((r) => setTimeout(r, wait));
        buy = await send({ buy: 1, price: amt, parameters: params });
      }
      if (buy?.error) throw new Error(buy.error.message || "Buy rejected");
      const cid = buy?.buy?.contract_id;
      if (!cid) throw new Error("Buy did not return a contract — try again");
      api.post("/track/trade", {
        account_id: accountId, contract_id: String(cid), symbol: playSym,
        contract_type: ct, stake: amt, payout: buy?.buy?.payout || amt,
        currency, strategy: "HiLo Cards 🎴", source: "hilo_cards", barrier: String(played),
      }).catch(() => {});
      // ⏳ wait for the 1-tick settle
      let poc = null;
      for (let i = 0; i < 20; i += 1) {
        await new Promise((r) => setTimeout(r, 1000));
        try {
          const res = await send({ proposal_open_contract: 1, contract_id: cid });
          poc = res?.proposal_open_contract;
          if (poc?.is_sold) break;
        } catch { /* retry */ }
      }
      const profit = Math.round(((poc?.profit != null ? Number(poc.profit) : -amt)) * 100) / 100;
      // New API returns `exit_spot`; legacy returned `exit_tick_display_value`
      const exitVal = poc?.exit_spot ?? poc?.exit_tick_display_value ?? poc?.exit_tick;
      const exitD = exitVal != null ? lastDigit(exitVal) : null;
      const won = profit > 0;
      api.patch(`/track/trade/${cid}`, { status: won ? "won" : "lost", profit }).catch(() => {});
      setNet((n) => Math.round((n + profit) * 100) / 100);
      setStreak((s) => {
        const ns = won ? s + 1 : 0;
        if (ns > bestStreak) { setBestStreak(ns); localStorage.setItem("hilocards_best", String(ns)); }
        return ns;
      });
      setHistory((h) => [{ id: cid, side, card: played, exit: exitD, profit, stake: amt, tie: exitD === played, t: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) }, ...h].slice(0, 30));
      if (exitD != null) { suitRef.current = SUITS[Math.floor(Math.random() * 4)]; setFlip((f) => !f); setCard(exitD); }
      if (won) toast.success(`🎴 ${exitD} — you called it! +$${profit.toFixed(2)}${streak + 1 >= 3 ? ` · 🔥 ${streak + 1} streak` : ""}`);
      else toast.error(exitD === played ? `🎴 ${exitD} — tie card, house takes it −$${amt.toFixed(2)}` : `🎴 ${exitD} — wrong call −$${amt.toFixed(2)}`);
    } catch (e) {
      toast.error(e?.message || "Trade rejected — try again");
    } finally {
      flightRef.current = false;
      setInFlight(null);
    }
  }, [authorized, card, stake, currency, accountId, bestStreak, streak, onConnect]);

  const disabledHi = card == null || card >= 9;
  const disabledLo = card == null || card <= 0;
  const red = suitRef.current === "♥" || suitRef.current === "♦";

  return (
    <div data-testid="hilocards-panel" className="rounded-lg border border-vts-line bg-vts-panel p-4 space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Dices className="w-4 h-4 text-amber-300" />
        <h3 className="text-sm font-bold text-white font-mono">🎴 HiLo CARDS</h3>
        <span className="text-[9px] font-mono text-vts-silver/50">real Digit Over/Under contracts · 🎯 {symLabel} · 1 tick</span>
        <span data-testid="hilocards-net" className={`ml-auto px-1.5 py-0.5 rounded text-[10px] font-bold font-mono ${net >= 0 ? "bg-vts-profit/10 text-vts-profit" : "bg-vts-loss/10 text-vts-loss"}`}>session {net >= 0 ? "+" : "−"}${Math.abs(net).toFixed(2)}</span>
      </div>

      {!authorized ? (
        <div className="text-center py-6 space-y-2">
          <p className="text-[11px] font-mono text-vts-silver/60">Connect your Deriv account (demo or real) to sit at the table.</p>
          <button data-testid="hilocards-connect" onClick={onConnect} className="h-9 px-4 rounded-md bg-amber-500/90 hover:bg-amber-500 text-black text-[11px] font-bold font-mono uppercase">Connect to play</button>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-center gap-6">
            <div data-testid="hilocards-card" className={`relative w-28 h-40 rounded-xl border-2 flex flex-col items-center justify-center select-none transition-transform duration-300 ${flip ? "rotate-1" : "-rotate-1"} ${inFlight ? "border-amber-400/70 bg-amber-400/5 animate-pulse" : "border-vts-line bg-vts-surface"}`}>
              <span className={`absolute top-2 left-2 text-sm ${red ? "text-red-400" : "text-vts-silver/70"}`}>{suitRef.current}</span>
              <span className={`text-6xl font-bold font-mono ${red ? "text-red-400" : "text-white"}`}>{card ?? "?"}</span>
              <span className={`absolute bottom-2 right-2 text-sm rotate-180 ${red ? "text-red-400" : "text-vts-silver/70"}`}>{suitRef.current}</span>
            </div>
            <div className="space-y-2">
              <p className="text-[10px] font-mono text-vts-silver/60 max-w-[180px]">Will the next digit land <b className="text-white">higher</b> or <b className="text-white">lower</b> than the card?</p>
              <div data-testid="hilocards-streak" className="flex items-center gap-1 text-[10px] font-mono">
                <Flame className={`w-3.5 h-3.5 ${streak >= 3 ? "text-orange-400" : "text-vts-silver/30"}`} />
                <span className={streak >= 3 ? "text-orange-300 font-bold" : "text-vts-silver/60"}>streak {streak}</span>
                <span className="text-vts-silver/40">· best {bestStreak}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[9px] uppercase font-mono text-vts-silver/50">Stake $</span>
                <input data-testid="hilocards-stake" value={stake} onChange={(e) => setStake(e.target.value)} inputMode="decimal"
                  className="h-8 w-20 rounded-md border border-vts-line bg-vts-surface px-2 text-[12px] font-mono font-bold text-white" />
              </div>
            </div>
          </div>

          {/* 🎯 pair filter — every card & play follows the strongest market */}
          <MarketScanner id="hilocards" mode="hilo" bar={card ?? 4} onChange={onScan}
            note="O/U = share of digits above/below the bar in the last 50 ticks · recent history, NOT a prediction" />

          <div className="grid grid-cols-2 gap-2">
            <button data-testid="hilocards-higher" onClick={() => play("hi")} disabled={disabledHi || Boolean(inFlight)}
              className={`h-12 rounded-lg font-bold font-mono text-sm transition ${disabledHi ? "bg-vts-surface text-vts-silver/30 cursor-not-allowed" : "bg-emerald-500/90 hover:bg-emerald-500 text-black"}`}>
              ⬆ HIGHER {pays.hi ? <span className="text-[10px] font-normal">×{pays.hi.toFixed(2)}</span> : disabledHi ? <span className="text-[9px]">(impossible on 9)</span> : null}
            </button>
            <button data-testid="hilocards-lower" onClick={() => play("lo")} disabled={disabledLo || Boolean(inFlight)}
              className={`h-12 rounded-lg font-bold font-mono text-sm transition ${disabledLo ? "bg-vts-surface text-vts-silver/30 cursor-not-allowed" : "bg-red-500/90 hover:bg-red-500 text-white"}`}>
              ⬇ LOWER {pays.lo ? <span className="text-[10px] font-normal">×{pays.lo.toFixed(2)}</span> : disabledLo ? <span className="text-[9px]">(impossible on 0)</span> : null}
            </button>
          </div>
          <p className="text-[8.5px] font-mono text-vts-silver/40">Honest rules: payout shifts with the card (risky calls pay more) · the SAME digit landing again is a loss for both sides — that tie is the house edge · every flip is a real {currency} contract on your connected account and shows in History.</p>

          {history.length > 0 && (
            <div data-testid="hilocards-history" className="space-y-1 max-h-44 overflow-y-auto">
              {history.map((h) => (
                <div key={h.id} className="flex items-center gap-2 text-[10px] font-mono rounded border border-vts-line/40 bg-vts-surface/40 px-2 py-1">
                  <span className="text-vts-silver/50">{h.t}</span>
                  <span className="text-white">🎴{h.card} {h.side === "hi" ? "⬆" : "⬇"} → {h.exit ?? "?"}</span>
                  {h.tie && <span className="text-amber-300">tie</span>}
                  <span className={`ml-auto font-bold ${h.profit > 0 ? "text-vts-profit" : "text-vts-loss"}`}>{h.profit > 0 ? "+" : "−"}${Math.abs(h.profit).toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}

          {/* 🤖 AUTO 24/7 — server bot plays the cards for you */}
          <HiLoCardsAuto
            authorized={authorized}
            accountId={accountId}
            accessToken={accessToken}
            onConnect={onConnect}
          />

          {/* ⚡ Coin Combat — recoverable-payout ladder bot */}
          <CoinCombat
            authorized={authorized}
            accountId={accountId}
            accessToken={accessToken}
            onConnect={onConnect}
          />
        </>
      )}
    </div>
  );
};

export default HiLoCards;
